/* eslint-disable react/jsx-key */
import React, { useState, useRef, useMemo, useEffect } from 'react'
import * as d3 from 'd3'
import R from 'ramda'
import moment from 'moment'
import { Radio, Icon, Button, message } from 'antd'
import PieGraph from './PieGraph'
import MonthlyDataTable, { daysCount } from './MonthlyDataTable'
import PiaTableInfoAntd from './PiaTableInfoAntd'
import MonthlyGraphRect from './MonthlyGraphRect'
import PerfDisplayGrid from './PerfDisplayGrid'
import styled from 'styled-components'
import { strCol } from '@integec/grid-tools/lib/cols'
import CardsErrorDialog from 'card-data/CardErrorDialogAntd'
import Grid, {
  virtualizedGridRenderer,
  freeEditEngine,
} from '@integec/grid-tools'
import { formatNumber, hasAccesPermits } from 'core'
import * as ScreenPermissions from 'core/EventActions'
import { applyEdits } from '@integec/grid-tools/lib/editEngine'
import { saveDailyPerfData } from 'api/pgads'
const TotalDiv = styled.div`
  text-align: ${props => props.textAlign || 'end'};
  font-size: 0.85em;
  font-weight: normal;
  height: 18px;
  width: ${props => props.width};
  border-right: ${props => props.borderRight || '1px solid #ccc'};
  border-top: 1px solid #ccc;
  border-left: 1px solid #ccc;
  border-bottom: ${props => props.borderBottom};
  background-color: rgb(178, 178, 245);
  padding-right: 3px;
`

export default props => {
  const [view, setView] = useState(
    props.actionPermits.includes(ScreenPermissions.MONTHLYVIEW_DEFAULT_DETAILS)
      ? 'daily'
      : 'summary'
  )

  const hourRef = useRef()
  const dailyRef = useRef()
  const graphDivRef = useRef()
  const svgMonthlygraph = useRef()
  const perfDisplayGridRef = useRef()
  const perfMainCompRef = React.createRef()
  const MonthlyDataTableRef = React.createRef()
  const [dataErrors, setDataErrors] = useState([])
  const [disabledDailyDatilsRadio, setDisabledDailyDatilsRadio] = useState(true)
  const [perfEditInfo, setPerfEditInfo] = useState(
    freeEditEngine.generateInitialEditInfo()
  )
  const [dailyDisplayEditInfo, setDailyDisplayEditInfo] = useState(
    freeEditEngine.generateInitialEditInfo()
  )

  useEffect(
    _ => {
      let dailyDetailAvail = props.unitList.find(
        u => u.unitId === props.unit
      ) || { dailyDetail: 'N' }
      dailyDetailAvail = dailyDetailAvail.dailyDetail || 'N'
      if (dailyDetailAvail === 'N') {
        setDisabledDailyDatilsRadio(false)
        setView('summary')
      }
    },
    [props.unit, props.utility, props.unitList]
  )

  const [monthlydata, setMonthlyData] = useState([])
  useEffect(_ => setMonthlyData(props.monthlydata), [props.monthlydata])

  const margin = 10
  const fixedLimitValue = 0.000001
  // const width = props.width
  let width = props.width < 1000 ? 1000 : props.width - 30
  // const width = 400
  const height = 110
  let limit = parseFloat(formatNumber('0.0000')(props.limit || 0))

  limit = limit === 0.0 ? fixedLimitValue : limit

  const sizeByClientSpecific = props.actionPermits.find(
    f => f.indexOf('MONTHLY_VIEW_GRAPH_SIZE') === 0
  )
  if (sizeByClientSpecific) {
    const widthBySize = sizeByClientSpecific.substring(
      sizeByClientSpecific.lastIndexOf('_') + 1
    )
    width = parseFloat(widthBySize)
  }

  const editable = useMemo(
    _ => {
      const monthlyViewPermission = hasAccesPermits({
        authGodMode: false,
        actionPermits: props.actionPermits,
        eventActions: [ScreenPermissions.MONTHLY_OPERATION_SAVE_PERF_DATA],
        matchAny: true,
      })

      if (!monthlyViewPermission) return false

      if (props.isAdminTypeUser) return true

      if (props.lock === 'L') return false

      return true
    },
    [props.actionPermits, props.isAdminTypeUser, props.lock]
  )

  const days = parseInt(
    moment(props.date)
      .endOf('month')
      .format('DD')
  )

  const setXscale = useMemo(
    _ => [
      ...new Set(
        R.flatten(
          R.range(0, days + 1).map(n =>
            R.range(0, 100).map(m =>
              parseFloat((m !== 0 ? n + 0.01 * m : n).toFixed(2))
            )
          )
        )
        // .flat()
      ),
    ],
    [days]
  )
  const xScale = useMemo(
    _ =>
      d3
        .scaleBand()
        // .range([0, width - 100])
        .range([0, width + 500])
        .domain(setXscale.sort((a, b) => a - b))
        .padding(0),
    [setXscale, width]
  )

  const yScale = useMemo(
    _ =>
      d3
        .scaleLinear()
        .range([height - margin, 0])
        .domain([0, limit]),
    // .nice()
    [limit]
  )

  const xMonthlyScale = {
    bandwidth: _e => (xScale ? xScale(2) - xScale(1) : '0'),
  }

  // const gc = xMonthlyScale
  //   ? `${R.range(1, days + 1)
  //       .map(_i => `${xMonthlyScale.bandwidth()}px`)
  //       .reduce((i, j) => `${i} ${j}`)}`
  //   : 'auto'
  const bw = xMonthlyScale ? xMonthlyScale.bandwidth() : 10

  const totalHourWidth = `${R.range(0, days + 1).reduce((a, _b) => a + bw)}`
  props.setHoursWidth(totalHourWidth)
  const dayCount = daysCount(props.date)

  const saveDataAll = _ => {
    const {
      year,
      month = props.date.format('MM'),
      utility,
      unit,
      // shortName,
      apiTargetInfo,
      setError,
      setSpinning,
      // monthlydata,
      perfDisplay,
      unitShortNameMap,
    } = props

    const editedPerf = { data: perfDisplay, editInfo: perfEditInfo }
    const savePerf = applyEdits(editedPerf)

    const editedDailtDisp = {
      data: monthlydata,
      editInfo: dailyDisplayEditInfo,
    }
    const saveDailyPerf = applyEdits(editedDailtDisp)

    setSpinning(true)
    saveDailyPerfData(
      apiTargetInfo,
      year,
      month,
      utility,
      unit.substring(utility.length),
      unitShortNameMap[unit],
      { perfDisplay: savePerf, monthlyDisplays: saveDailyPerf }
    )().fork(setError, dt => {
      setSpinning(false)

      if (dt.length > 0) {
        setDataErrors(dt)
        if (
          dt.filter(d => d.severity === '1' || d.severity === 1).length === 0
        ) {
          setPerfEditInfo(freeEditEngine.generateInitialEditInfo())
          setDailyDisplayEditInfo(freeEditEngine.generateInitialEditInfo())
        }
        // props.retrieveData && props.retrieveData()
      } else {
        message.success('Data saved Successfully.')
        props.retrieveData && props.retrieveData()
        setPerfEditInfo(freeEditEngine.generateInitialEditInfo())
        setDailyDisplayEditInfo(freeEditEngine.generateInitialEditInfo())
      }
    })
  }

  const saveData = _ => {
    saveDataAll()
    // if (view === 'summary' && perfMainCompRef.current) {
    //   perfMainCompRef.current.saveData()
    // } else if (view === 'daily' && MonthlyDataTableRef.current) {
    //   MonthlyDataTableRef.current.saveData()
    // }
  }

  const minusWidth = 200

  return (
    <>
      <div
        style={{
          gridRowEnd: 'span 1',
          gridColumnEnd: 'span 1',
          display: 'grid',
          gridTemplateColumns: '100px min-content 50px',
          gridTemplateRows: '130px auto',
        }}
      >
        <Icon
          type={props.lock === 'L' ? 'lock' : 'unlock'}
          style={{
            position: 'absolute',
            fontSize: '30px',
            top: '42px',
            left: '10px',
          }}
          theme="twoTone"
        />

        <svg>
          {props.isValid && (
            <text x={50} y={60}>
              MW
            </text>
          )}
          <g transform={`translate(${100}, ${10})`}>
            {props.isValid && (
              <text x={xScale(0) - 20} y={limit === 0 ? 10 : yScale(0)}>
                0
              </text>
            )}
            {limit !== 0.0 && (
              <text
                x={
                  xScale(0) -
                  10 -
                  (limit === fixedLimitValue ? 2 : `${limit}`.length) * 8
                }
                y={yScale(limit) + 5}
              >
                {/* {formatNumber('0.0000')(limit)} */}
                {limit === fixedLimitValue ? 0 : limit}
              </text>
            )}
            {props.isValid && (
              <polyline
                fill="none"
                stroke="black"
                points={`${xScale(0) - 10},${yScale(0)} ${xScale(0)},${yScale(
                  0
                )} ${xScale(0)},${yScale(limit)} ${xScale(0) - 10},${yScale(
                  limit
                )}`}
              />
            )}
          </g>
        </svg>
        <div
          ref={graphDivRef}
          style={{
            gridColumnEnd: 'span 1',
            overflowX: 'auto',
            maxWidth: props.width - minusWidth,
            overflowY: 'hidden',
          }}
          onScroll={() => {
            if (dailyRef.current) {
              dailyRef.current.scrollLeft = graphDivRef.current.scrollLeft
            }
            if (hourRef.current)
              hourRef.current.scrollLeft = graphDivRef.current.scrollLeft
            if (perfDisplayGridRef.current) {
              perfDisplayGridRef.current.scrollLeft =
                graphDivRef.current.scrollLeft
            }
          }}
        >
          <svg
            ref={svgMonthlygraph}
            style={{
              // backgroundColor: 'yellow',
              height: height + 5,
              paddingBottom: '-10px',
              // width: `${R.range(0, days + 1).reduce((a, _b) => a + bw)}px`,
              width: `${totalHourWidth}px`,
            }}
          >
            {!props.isValid && (
              <text x={20} y={60}>
                Net Dependable Capacity is not valid, Please update Net
                Dependable Capacity or check Card 95 Performance Data for the
                same unit and month.
              </text>
            )}
            {props.isValid && (
              <MonthlyGraphRect
                year={props.year}
                utility={props.utility}
                unit={props.unit}
                setCardEditPromptRowData={props.setCardEditPromptRowData}
                setCardEditPrompt={props.setCardEditPrompt}
                setCardEditPromptMode={props.setCardEditPromptMode}
                cardData={props.cardData}
                // data={getData(props.data).sort(
                //   (a, b) => a.startDay - b.startDay
                // )}
                data={props.data}
                xScale={xScale}
                yScale={yScale}
                limit={parseFloat(limit)}
                margin={margin}
                height={height}
                setMonthlyGraphRectSelection={
                  props.setMonthlyGraphRectSelection
                }
                days={days}
                lock={props.lock}
                selectedEventNum={props.selectedEventNum}
                envConfig={props.envConfig}
                actionPermits={props.actionPermits}
                isAdminTypeUser={props.isAdminTypeUser}
              />
            )}
          </svg>
        </div>
        <div />
      </div>
      <PieGraph
        data={props.tmpMonthlyPie}
        style={{ gridRowEnd: 'span 1', marginTop: '10px' }}
      />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '100px min-content 50px',
        }}
      >
        {view === 'daily' ? (
          <div
            style={{
              textAlign: 'center',
              borderTop: '0.2px solid #cec7c7',
              borderLeft: '0.2px solid #cec7c7',
              borderBottom: '0.2px solid #cec7c7',
              fontSize: 'small',
              backgroundColor: '#eee',
              color: 'black',
            }}
          >
            Items
          </div>
        ) : (
          <div />
        )}
        <div
          ref={hourRef}
          style={{
            // minHeight: '30px',
            // minWidth: bw * 30 + 'px',
            width: props.width - minusWidth + 'px',
            maxWidth: bw * dayCount + 'px',
            overflow: 'hidden',
            // maxHeight: '20px',
          }}
        >
          <Grid
            data={[
              R.range(1, dayCount + 1)
                .map(i => ({ ['col' + i]: i }))
                .reduce((a, b) => ({ ...a, ...b })),
            ]}
            headers={R.range(1, dayCount + 1).map(i =>
              strCol({
                ident: 'col' + i,
                display: '' + i,
                width: bw,
                textAlign: 'center',
                borderTop: '0.2px solid #cec7c7',
                borderLeft: '0.2px solid #cec7c7',
                borderBottom: '0.2px solid #cec7c7',
                border: '0.2px solid #cec7c7',

                fontSize: 'small',
                backgroundColor: '#eee',
                color: 'black',
              })
            )}
            render={virtualizedGridRenderer({
              headerRowHeight: 0,
              rowHeight: 22,
              height: 23,
              width: bw * dayCount,
            })}
            // render={p =>
            //   virtualizedGridRenderer({
            //     ...p,
            //     height: 40,
            //     width: 200,
            //     headerRowHeight: 20,
            //   })
            // }
            width={bw * dayCount}
            height={23}
          />
        </div>

        {view === 'daily' ? (
          <div
            style={{
              textAlign: 'center',
              borderTop: '1px solid #ccc',
              borderRight: '1px solid #ccc',
              borderBottom: '1px solid #ccc',
              borderLeft: '1px solid #ccc',
              fontSize: 'small',
              backgroundColor: '#eee',
              color: 'black',
              width: '80px',
            }}
          >
            Total
          </div>
        ) : (
          <div />
        )}
      </div>
      {/* <div style={{ display: 'grid', alignContent: 'space-begtween', marginBottom: '2px' }}> */}
      <Radio.Group
        style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}
        onChange={e => setView(e.target.value)}
        value={view}
        buttonStyle="solid"
        size="small"
      >
        <Radio size="small" value="summary">
          Summary
        </Radio>
        <Radio size="small" value="daily" disabled={!disabledDailyDatilsRadio}>
          Daily Detail
        </Radio>
      </Radio.Group>
      {/* </div> */}
      {view === 'daily' && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '100px min-content 80px',
          }}
        >
          <div style={{ maxWidth: '100px' }}>
            {monthlydata.map((d, i) => (
              <TotalDiv
                borderBottom={
                  i === monthlydata.length - 1 ? '1px solid #cccccc' : undefined
                }
                borderRight="0px solid #cccccc"
              >
                {d.ROWHEADER}
              </TotalDiv>
            ))}
          </div>

          <div
            ref={dailyRef}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              minWidth: '30px',
              maxWidth: props.width - minusWidth,
              overflowX: 'hidden',
              overflowY: 'hidden',
            }}
          >
            <MonthlyDataTable
              ref={MonthlyDataTableRef}
              // data={props.monthlydata}
              data={monthlydata}
              setData={setMonthlyData}
              editInfo={dailyDisplayEditInfo}
              setEditInfo={setDailyDisplayEditInfo}
              date={props.date}
              screenSize={{
                ...props.screenSize,
                width: props.screenSize.width - minusWidth,
                hrColumnWidth: { bw },
              }}
              hrColumnWidth={bw}
              apiTargetInfo={props.apiTargetInfo}
              year={props.date.format('YYYY')}
              month={props.date.format('MM')}
              utility={props.utility}
              unit={props.unit}
              setError={props.setError}
              error={props.error}
              setSpinning={props.setSpinning}
              spinning={props.spinning}
              retrieveData={props.retrieveData}
            />
          </div>
          <div style={{ maxWidth: '80px' }}>
            {monthlydata.map((d, i) => (
              <TotalDiv
                textAlign="center"
                borderBottom={
                  i === monthlydata.length - 1 ? '1px solid #cccccc' : undefined
                }
              >
                {d.TOTAL}
              </TotalDiv>
            ))}
          </div>
        </div>
      )}
      {view === 'summary' && (
        <PerfDisplayGrid
          apiTargetInfo={props.apiTargetInfo}
          cref={perfDisplayGridRef}
          ref={perfMainCompRef}
          data={props.perfDisplay}
          editInfo={perfEditInfo}
          setEditInfo={setPerfEditInfo}
          year={props.date.format('YYYY')}
          month={props.date.format('MM')}
          utility={props.utility}
          unit={props.unit}
          setError={props.setError}
          error={props.error}
          setSpinning={props.setSpinning}
          spinning={props.spinning}
          tmpMonthlyPie={props.tmpMonthlyPie}
          monthlydata={monthlydata}
          screenSize={{
            width: totalHourWidth,
            maxWidth: props.width - minusWidth,
          }}
          retrieveData={props.retrieveData}
          envConfig={props.envConfig}
          actionPermits={props.actionPermits}
        />
      )}
      {/* {view === 'daily' && <PiaTableInfoAntd data={props.tmpMonthlyPie} />} */}
      <div style={{ display: 'grid', alignContent: 'baseline' }}>
        <PiaTableInfoAntd data={props.tmpMonthlyPie} />
        <Button
          style={{ marginTop: '5px' }}
          type="primary"
          onClick={saveData}
          disabled={!editable}
          icon="save"
        >
          Save
        </Button>
        {dataErrors.length > 0 && (
          <CardsErrorDialog
            keys="card97errordialog"
            errors={dataErrors}
            closeErrorPrompt={_ => setDataErrors([])}
            action="update"
            visible={dataErrors.length > 0}
            setShowEditDialog={_ => setDataErrors([])}
            retrieveData={props.retrieveData}
          />
        )}
      </div>
    </>
  )
}
